import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Input } from '@medikei/ui';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalFooter } from '@medikei/ui';
import { AlertCircle, Key, CheckCircle2 } from 'lucide-react';
import { useRequestPayout } from '../hooks/usePartnerPayouts';
import { usePartnerProfile } from '../hooks/usePartnerProfile';
import { partnerService } from '../services/partner.service';
import { useQueryClient } from '@tanstack/react-query';
import type { CreatePayoutRequest } from '../types/partner';

const MINIMUM_PAYOUT_AMOUNT = 50; // R$ 50,00 = 5.000 centavos

const PIX_KEY_TYPES = [
  { value: 'CPF', label: 'CPF' },
  { value: 'CNPJ', label: 'CNPJ' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'PHONE', label: 'Telefone' },
  { value: 'EVP', label: 'Chave Aleatória (EVP)' },
] as const;

type PixType = 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP';

const payoutSchema = z.object({
  amount: z
    .number({ invalid_type_error: 'Informe um valor válido' })
    .min(MINIMUM_PAYOUT_AMOUNT, 'O valor mínimo para solicitação de saque é R$ 50,00 (5.000 centavos)'),
  paymentMethod: z.enum(['PIX', 'BANK_TRANSFER']),
  pixKey: z.string().optional(),
  pixKeyType: z.enum(['CPF', 'CNPJ', 'EMAIL', 'PHONE', 'EVP']).optional(),
  bankAccountId: z.string().optional(),
});

type PayoutFormData = z.infer<typeof payoutSchema>;

interface PayoutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableBalance: number;
  pendingBalance?: number;
}

export function PayoutModal({
  open,
  onOpenChange,
  availableBalance,
  pendingBalance = 0,
}: PayoutModalProps) {
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [savingPixKey, setSavingPixKey] = useState(false);
  const [newPixType, setNewPixType] = useState<PixType>('CPF');
  const [newPixKey, setNewPixKey] = useState('');
  const [pixError, setPixError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const requestPayout = useRequestPayout();
  const { data: profile } = usePartnerProfile(open);

  const existingPixKey = profile?.pixKey?.key || (profile as any)?.pixKey;
  const hasPixKey = Boolean(existingPixKey);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<PayoutFormData>({
    resolver: zodResolver(payoutSchema),
    defaultValues: {
      amount: availableBalance >= MINIMUM_PAYOUT_AMOUNT ? availableBalance : MINIMUM_PAYOUT_AMOUNT,
      paymentMethod: 'PIX',
    },
  });

  const paymentMethod = watch('paymentMethod');
  const amount = watch('amount');

  const handleSavePixKey = async () => {
    if (!newPixKey.trim()) {
      setPixError('Por favor, digite uma chave PIX válida.');
      return;
    }
    setSavingPixKey(true);
    setPixError(null);
    try {
      await partnerService.addPixKey({
        type: newPixType,
        key: newPixKey.trim(),
      });
      await queryClient.invalidateQueries({ queryKey: ['partner', 'profile'] });
      setValue('pixKey', newPixKey.trim());
      setValue('pixKeyType', newPixType);
    } catch (err: any) {
      setPixError(err?.response?.data?.message || 'Erro ao cadastrar chave PIX. Tente novamente.');
    } finally {
      setSavingPixKey(false);
    }
  };

  const onSubmit = async (data: PayoutFormData) => {
    // Client-side validation: minimum amount check
    if (data.amount < MINIMUM_PAYOUT_AMOUNT) {
      return;
    }
    // Client-side validation: available balance check
    if (data.amount > availableBalance) {
      return;
    }
    // Client-side validation: PIX key check
    if (data.paymentMethod === 'PIX' && !hasPixKey && !data.pixKey) {
      if (!newPixKey.trim()) {
        setPixError('Por favor, cadastre uma chave PIX antes de solicitar o saque.');
        return;
      }
      try {
        await partnerService.addPixKey({
          type: newPixType,
          key: newPixKey.trim(),
        });
        await queryClient.invalidateQueries({ queryKey: ['partner', 'profile'] });
      } catch (err: any) {
        setPixError(err?.response?.data?.message || 'Erro ao cadastrar chave PIX.');
        return;
      }
    }

    try {
      const payload: CreatePayoutRequest = {
        amount: data.amount,
        paymentMethod: data.paymentMethod,
        pixKey: data.pixKey || existingPixKey || newPixKey.trim(),
        pixKeyType: data.pixKeyType || profile?.pixKey?.type || newPixType,
      };
      await requestPayout.mutateAsync(payload);
      setStep('success');
      reset();
    } catch (error) {
      console.error('Erro ao solicitar saque:', error);
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setStep('form');
      setNewPixKey('');
      setPixError(null);
      reset();
    }
    onOpenChange(isOpen);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const isAmountBelowMinimum = amount < MINIMUM_PAYOUT_AMOUNT;
  const isAmountExceedingBalance = amount > availableBalance;
  const isBalanceInsufficient = availableBalance < MINIMUM_PAYOUT_AMOUNT;

  return (
    <Modal open={open} onOpenChange={handleClose}>
      <ModalContent className="max-w-lg">
        {step === 'form' ? (
          <>
            <ModalHeader>
              <ModalTitle>Solicitar Saque</ModalTitle>
              <ModalDescription>
                Escolha o valor e método de recebimento.
                <br />
                Saldo livre disponível: <strong className="text-foreground">{formatCurrency(availableBalance)}</strong>
              </ModalDescription>
            </ModalHeader>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {isBalanceInsufficient && (
                <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-sm flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  <div>
                    <strong className="font-bold block text-foreground">Saldo Insuficiente para Saque</strong>
                    O valor mínimo para solicitação de saque é de R$ 50,00. Seu saldo disponível atual é de {formatCurrency(availableBalance)}.
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-foreground">
                  Valor do Saque (R$) <span className="text-destructive">*</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min={MINIMUM_PAYOUT_AMOUNT}
                  max={availableBalance}
                  {...register('amount', { valueAsNumber: true })}
                  placeholder="50,00"
                  className="rounded-xl"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Valor mínimo: R$ 50,00 &bull; Saldo livre: <strong className="text-foreground">{formatCurrency(availableBalance)}</strong>
                </p>
                {errors.amount?.message && (
                  <p className="text-xs text-destructive mt-1">{errors.amount.message}</p>
                )}
              </div>

              {isAmountExceedingBalance && !isBalanceInsufficient && (
                <p className="text-xs sm:text-sm text-destructive font-medium flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  Valor excede o saldo livre disponível de {formatCurrency(availableBalance)}
                </p>
              )}

              {isAmountBelowMinimum && (
                <p className="text-xs sm:text-sm text-destructive font-medium flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  O valor mínimo permitido para solicitação de saque é R$ 50,00
                </p>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-foreground">
                  Método de Recebimento
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setValue('paymentMethod', 'PIX')}
                    className={`p-3.5 sm:p-4 rounded-2xl border text-left transition-all cursor-pointer ${
                      paymentMethod === 'PIX'
                        ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/25 shadow-xs font-semibold'
                        : 'border-border bg-card text-foreground hover:bg-muted/40'
                    }`}
                  >
                    <span className="font-bold block text-sm">PIX</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      Transferência instantânea
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setValue('paymentMethod', 'BANK_TRANSFER')}
                    className={`p-3.5 sm:p-4 rounded-2xl border text-left transition-all cursor-pointer ${
                      paymentMethod === 'BANK_TRANSFER'
                        ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/25 shadow-xs font-semibold'
                        : 'border-border bg-card text-foreground hover:bg-muted/40'
                    }`}
                  >
                    <span className="font-bold block text-sm">Transferência</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      Até 2 dias úteis
                    </span>
                  </button>
                </div>
              </div>

              {paymentMethod === 'PIX' && (
                <div className="space-y-3 p-4 rounded-2xl bg-muted/40 border border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm font-bold text-foreground flex items-center gap-2">
                      <Key className="w-4 h-4 text-primary" />
                      Chave PIX para Recebimento
                    </span>
                    {hasPixKey && (
                      <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Cadastrada
                      </span>
                    )}
                  </div>

                  {hasPixKey ? (
                    <div className="text-sm bg-card p-3 rounded-xl border border-border">
                      <p className="text-xs text-muted-foreground">Chave PIX atual:</p>
                      <p className="font-bold font-mono text-foreground mt-0.5 text-sm sm:text-base">
                        {profile?.pixKey?.maskedKey || profile?.pixKey?.key || existingPixKey}
                        {profile?.pixKey?.type ? ` (${profile.pixKey.type})` : ''}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 sm:p-3.5">
                      <div className="flex items-start gap-2 text-amber-600 dark:text-amber-400 text-xs">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <div>
                          <strong className="text-foreground">Cadastre sua Chave PIX:</strong> Selecione o tipo e informe sua chave abaixo para receber o pagamento.
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-foreground block">Tipo de Chave PIX</label>
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
                          {PIX_KEY_TYPES.map((item) => (
                            <button
                              key={item.value}
                              type="button"
                              onClick={() => setNewPixType(item.value as PixType)}
                              className={`py-1.5 px-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                                newPixType === item.value
                                  ? 'bg-primary text-primary-foreground border-primary shadow-xs'
                                  : 'bg-card text-muted-foreground border-border hover:bg-muted hover:text-foreground'
                              }`}
                            >
                              {item.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-foreground block mb-1">
                          Chave PIX ({newPixType})
                        </label>
                        <div className="flex gap-2">
                          <Input
                            value={newPixKey}
                            onChange={(e) => setNewPixKey(e.target.value)}
                            placeholder={
                              newPixType === 'CPF'
                                ? '000.000.000-00'
                                : newPixType === 'CNPJ'
                                ? '00.000.000/0001-00'
                                : newPixType === 'EMAIL'
                                ? 'seu-email@exemplo.com'
                                : newPixType === 'PHONE'
                                ? '(11) 99999-9999'
                                : 'Chave aleatória EVP'
                            }
                            className="bg-card text-sm rounded-xl flex-1"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={handleSavePixKey}
                            loading={savingPixKey}
                            className="rounded-xl font-semibold px-4"
                          >
                            Salvar
                          </Button>
                        </div>
                      </div>

                      {pixError && (
                        <p className="text-xs text-destructive font-medium">{pixError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {pendingBalance > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 p-3 rounded-xl border border-amber-500/20">
                  Obs: Você possui <strong>{formatCurrency(pendingBalance)}</strong> em comissões pendentes de liberação.
                </p>
              )}

              <ModalFooter>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => handleClose(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  loading={requestPayout.isPending}
                  disabled={
                    isAmountBelowMinimum ||
                    isAmountExceedingBalance ||
                    (paymentMethod === 'PIX' && !hasPixKey && !newPixKey.trim())
                  }
                >
                  Solicitar Saque
                </Button>
              </ModalFooter>
            </form>
          </>
        ) : (
          <>
            <ModalHeader>
              <ModalTitle className="text-center text-emerald-600">
                Saque Solicitado com Sucesso!
              </ModalTitle>
              <ModalDescription className="text-center">
                Sua solicitação de saque de {formatCurrency(amount)} foi recebida e processada.
                O valor será creditado em sua conta conforme a modalidade escolhida.
              </ModalDescription>
            </ModalHeader>
            <div className="flex justify-center py-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-emerald-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <ModalFooter>
              <Button onClick={() => handleClose(false)} className="w-full">
                Fechar
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
